import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import { awardXP, getTodayChallengeGame, completeDailyChallenge } from '../utils/xp';
import { loadGamePool, getTranslationMap } from '../data/gameWords';
import { getWheelPuzzles } from '../data/wordWheelData';
import './WordWheel.css';

const TEXT = {
    title: { en: 'Word Wheel', it: 'Ruota delle parole', es: 'Rueda de palabras', fr: 'Roue des mots', pt: 'Roda de palavras' },
    loading: { en: 'Preparing your wheel...', it: 'Sto preparando la ruota...', es: 'Preparando tu rueda...', fr: 'Préparation de votre roue...', pt: 'Preparando a sua roda...' },
    tooShort: { en: 'Too short', it: 'Troppo corta', es: 'Muy corta', fr: 'Trop court', pt: 'Muito curta' },
    already: { en: 'Already found!', it: 'Già trovata!', es: '¡Ya la encontraste!', fr: 'Déjà trouvé !', pt: 'Já encontrada!' },
    nice: { en: 'Nice! ✓', it: 'Bene! ✓', es: '¡Bien! ✓', fr: 'Bien ! ✓', pt: 'Muito bem! ✓' },
    bonusWord: { en: 'Bonus word! ✓', it: 'Parola bonus! ✓', es: '¡Palabra extra! ✓', fr: 'Mot bonus ! ✓', pt: 'Palavra extra! ✓' },
    pangramMsg: { en: '🌟 Pangram! Great word!', it: '🌟 Pangramma! Ottima parola!', es: '🌟 ¡Pangrama! ¡Gran palabra!', fr: '🌟 Pangramme ! Super mot !', pt: '🌟 Pangrama! Ótima palavra!' },
    invalid: { en: 'Not a valid word', it: 'Parola non valida', es: 'Palabra no válida', fr: 'Mot non valide', pt: 'Palavra inválida' },
    prompt: { en: 'Tap letters to spell a word', it: 'Tocca le lettere per formare una parola', es: 'Toca las letras para formar una palabra', fr: 'Touchez les lettres pour former un mot', pt: 'Toque nas letras para formar uma palavra' },
    enter: { en: 'Enter', it: 'Invio', es: 'Enviar', fr: 'Valider', pt: 'Enviar' },
    finish: { en: 'Finish', it: 'Fine', es: 'Terminar', fr: 'Terminer', pt: 'Terminar' },
    niceWork: { en: 'Nice work!', it: 'Ottimo lavoro!', es: '¡Buen trabajo!', fr: 'Beau travail !', pt: 'Bom trabalho!' },
    wordsFound: { en: 'words found', it: 'parole trovate', es: 'palabras encontradas', fr: 'mots trouvés', pt: 'palavras encontradas' },
    bonus: { en: 'bonus', it: 'bonus', es: 'extra', fr: 'bonus', pt: 'extra' },
    pangrams: { en: 'pangrams', it: 'pangrammi', es: 'pangramas', fr: 'pangrammes', pt: 'pangramas' },
    missed: { en: 'Words you missed', it: 'Parole che ti sono sfuggite', es: 'Palabras que te faltaron', fr: 'Mots que vous avez manqués', pt: 'Palavras que você perdeu' },
    again: { en: 'Play again →', it: 'Gioca ancora →', es: 'Jugar otra vez →', fr: 'Rejouer →', pt: 'Jogar de novo →' },
    back: { en: 'Back to Games', it: 'Torna ai giochi', es: 'Volver a los juegos', fr: 'Retour aux jeux', pt: 'Voltar aos jogos' },
    saved: { en: '✓ Saved!', it: '✓ Salvata!', es: '✓ ¡Guardada!', fr: '✓ Enregistré !', pt: '✓ Salva!' },
};

const stripAccents = (w) => String(w || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const capitalize = (l) => l.charAt(0).toUpperCase() + l.slice(1);

function canMake(key, letters) {
    const counts = {};
    letters.forEach(l => { counts[l] = (counts[l] || 0) + 1; });
    for (const ch of key) {
        if (!counts[ch]) return false;
        counts[ch] -= 1;
    }
    return true;
}

// Turns one stored puzzle into what the screen needs.
// Words the learner already knows (from lessons and the Word Bank) that fit the letters are accepted as bonus words.
function buildSet(raw, translations, personal) {
    const letters = raw.letters.toUpperCase().split('');
    const lower = letters.map(l => l.toLowerCase());
    const entries = {};
    const words = raw.words.map(word => {
        const key = stripAccents(word);
        const entry = { word, key, translation: translations[key] || '', pangram: key.length === letters.length, bonus: false };
        entries[key] = entry;
        return entry;
    });
    raw.extra.forEach(word => {
        const key = stripAccents(word);
        if (entries[key]) return;
        entries[key] = { word, key, translation: translations[key] || '', pangram: key.length === letters.length, bonus: true };
    });
    personal.forEach(item => {
        if (!item.word || /\s/.test(item.word.trim())) return;
        const key = stripAccents(item.word.trim());
        if (key.length < 3 || key.length > letters.length || !/^[a-z]+$/.test(key)) return;
        if (!canMake(key, lower)) return;
        if (entries[key]) {
            if (item.translation && !entries[key].translation) entries[key].translation = item.translation;
            return;
        }
        entries[key] = { word: item.word.trim(), key, translation: item.translation || '', pangram: key.length === letters.length, bonus: true };
    });
    return { letters, words, entries };
}

// Prefers a puzzle that has some of the learner's own words in it, so the game reinforces what they study
function pickPuzzle(puzzles, personal) {
    const personalKeys = personal.map(p => stripAccents(p.word)).filter(k => k.length >= 3);
    if (personalKeys.length > 0 && Math.random() < 0.6) {
        const scored = puzzles
            .map(p => {
                const lower = p.letters.toLowerCase().split('');
                return { p, score: personalKeys.filter(k => canMake(k, lower)).length };
            })
            .filter(x => x.score > 0);
        if (scored.length > 0) return scored[Math.floor(Math.random() * scored.length)].p;
    }
    return puzzles[Math.floor(Math.random() * puzzles.length)];
}

export default function WordWheel() {
    const navigate = useNavigate();
    const { activeLanguage } = useUserLanguage();
    const { interfaceLanguage } = useLanguage();
    const uiLang = interfaceLanguage || 'en';
    const tx = (key) => (TEXT[key] && (TEXT[key][uiLang] || TEXT[key].en)) || key;
    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
    const targetLangName = capitalize(targetLang);
    const [gamePool, setGamePool] = useState(null);
    const [set, setSet] = useState(null);
    const [usedIndices, setUsedIndices] = useState([]);
    const [currentWord, setCurrentWord] = useState('');
    const [foundWords, setFoundWords] = useState([]);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [finished, setFinished] = useState(false);
    const [savingWord, setSavingWord] = useState(null);

    const startPuzzle = (pool) => {
        const puzzles = getWheelPuzzles(targetLang);
        if (puzzles.length === 0) return;
        const translations = getTranslationMap(targetLang, interfaceLanguage);
        const raw = pickPuzzle(puzzles, pool.personal);
        setSet(buildSet(raw, translations, pool.personal));
        setUsedIndices([]);
        setCurrentWord('');
        setFoundWords([]);
        setFinished(false);
    };

    useEffect(() => {
        const init = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            const level = localStorage.getItem('vozoly_level') || 'A1';
            const loaded = await loadGamePool(user ? user.id : null, targetLang, interfaceLanguage, level);
            setGamePool(loaded);
            const puzzles = getWheelPuzzles(targetLang);
            if (puzzles.length === 0) return;
            const translations = getTranslationMap(targetLang, interfaceLanguage);
            const raw = pickPuzzle(puzzles, loaded.personal);
            setSet(buildSet(raw, translations, loaded.personal));
        };
        init();
    }, [targetLang, interfaceLanguage]);

    const positions = useMemo(() => {
        if (!set) return [];
        const n = set.letters.length;
        const radius = 88;
        return set.letters.map((_, i) => {
            const angle = (i / n) * 2 * Math.PI - Math.PI / 2;
            return {
                left: 100 + radius * Math.cos(angle),
                top: 100 + radius * Math.sin(angle),
            };
        });
    }, [set]);

    const flashMessage = (text, type) => {
        setMessage(text);
        setMessageType(type);
        setTimeout(() => setMessage(''), 1400);
    };

    const tapLetter = (index) => {
        if (usedIndices.includes(index)) return;
        setUsedIndices(prev => [...prev, index]);
        setCurrentWord(prev => prev + set.letters[index]);
    };

    const backspace = () => {
        setUsedIndices(prev => prev.slice(0, -1));
        setCurrentWord(prev => prev.slice(0, -1));
    };

    const clearWord = () => {
        setUsedIndices([]);
        setCurrentWord('');
    };

    const shuffle = () => {
        setSet(prev => ({ ...prev, letters: [...prev.letters].sort(() => Math.random() - 0.5) }));
        clearWord();
    };

    const submitWord = () => {
        const key = currentWord.toLowerCase();
        if (key.length < 3) {
            flashMessage(tx('tooShort'), 'error');
            clearWord();
            return;
        }
        if (foundWords.includes(key)) {
            flashMessage(tx('already'), 'error');
            clearWord();
            return;
        }
        const entry = set.entries[key];
        if (entry) {
            setFoundWords(prev => [...prev, key]);
            flashMessage(entry.pangram ? tx('pangramMsg') : (entry.bonus ? tx('bonusWord') : tx('nice')), 'success');
        } else {
            flashMessage(tx('invalid'), 'error');
        }
        clearWord();
    };

    const finishGame = () => {
        setFinished(true);
        const xp = Math.min(25, foundWords.length * 3);
        if (xp > 0) awardXP(xp, 'word-wheel');
        if (foundWords.length >= 3 && getTodayChallengeGame() === 'word-wheel') {
            completeDailyChallenge('word-wheel');
        }
    };

    const saveFoundWord = async (key) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const info = set.entries[key];
        const word = info.word;
        await supabase.from('word_bank').upsert({
            user_id: user.id,
            word,
            translation: info.translation || null,
            target_language: targetLang,
            source: 'manual',
            entry_type: 'word',
        }, { onConflict: 'user_id,word,target_language', ignoreDuplicates: true });
        setSavingWord(key);
        setTimeout(() => setSavingWord(null), 1200);

        fetch('/api/identify-word', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ word, language: targetLangName, interfaceLanguage }),
        })
            .then(r => r.json())
            .then(async result => {
                if (!result.valid) return;
                await supabase.from('word_bank').upsert({
                    user_id: user.id,
                    word,
                    translation: result.translation,
                    phonetic: result.phonetic,
                    type: result.type,
                    note: result.note || '',
                    target_language: targetLang,
                    examples: result.examples || [],
                    entry_type: 'word',
                    source: 'manual',
                }, { onConflict: 'user_id,word,target_language' });
            })
            .catch(() => { });
    };

    if (!set) {
        return (
            <div className="ww-screen">
                <div className="ww-top-bar">
                    <button className="ww-back" onClick={() => navigate('/games')}>←</button>
                    <div className="ww-title">{tx('title')}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="ww-complete">
                    <div className="ww-complete-stat">{tx('loading')}</div>
                </div>
            </div>
        );
    }

    const totalWords = set.words.length;
    const foundTargets = foundWords.filter(k => !set.entries[k].bonus).length;
    const foundBonus = foundWords.length - foundTargets;
    const totalPangrams = set.words.filter(w => w.pangram).length;
    const foundPangrams = foundWords.filter(k => set.entries[k].pangram && !set.entries[k].bonus).length;

    if (finished) {
        return (
            <div className="ww-screen">
                <div className="ww-complete">
                    <div className="ww-complete-icon">🔤</div>
                    <div className="ww-complete-title">{tx('niceWork')}</div>
                    <div className="ww-complete-stat">
                        {foundTargets} / {totalWords} {tx('wordsFound')}{foundBonus > 0 ? ` · +${foundBonus} ${tx('bonus')}` : ''}
                    </div>
                    {totalPangrams > 0 && (
                        <div className="ww-complete-stat">{foundPangrams} / {totalPangrams} {tx('pangrams')} 🌟</div>
                    )}
                    <div className="ww-complete-xp">+{Math.min(25, foundWords.length * 3)} XP</div>

                    {foundTargets < totalWords && (
                        <div className="ww-missed">
                            <div className="ww-missed-label">{tx('missed')}</div>
                            {set.words.filter(w => !foundWords.includes(w.key)).slice(0, 12).map(w => (
                                <div key={w.key} className="ww-missed-row">
                                    <span className="ww-missed-word">{w.word}</span>
                                    <span className="ww-missed-translation">{w.translation}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <button className="ww-play-again" onClick={() => startPuzzle(gamePool)}>{tx('again')}</button>
                    <button className="ww-back-btn" onClick={() => navigate('/games')}>{tx('back')}</button>
                </div>
            </div>
        );
    }

    return (
        <div className="ww-screen">
            <div className="ww-top-bar">
                <button className="ww-back" onClick={() => navigate('/games')}>←</button>
                <div className="ww-title">{tx('title')}</div>
                <button className="ww-finish-btn" onClick={finishGame}>{tx('finish')}</button>
            </div>

            <div className="ww-found-count">
                {foundTargets} / {totalWords} {tx('wordsFound')}
                {foundBonus > 0 && ` · +${foundBonus} ${tx('bonus')}`}
                {totalPangrams > 0 && ` · ${foundPangrams}/${totalPangrams} 🌟`}
            </div>

            <div className="ww-current-word-wrap">
                <div className={`ww-current-word ${messageType}`}>
                    {message || currentWord || tx('prompt')}
                </div>
            </div>

            <div className="ww-wheel">
                {set.letters.map((letter, i) => (
                    <button
                        key={i}
                        className={`ww-letter ${usedIndices.includes(i) ? 'used' : ''}`}
                        style={{ left: positions[i].left, top: positions[i].top }}
                        onClick={() => tapLetter(i)}
                        disabled={usedIndices.includes(i)}
                    >
                        {letter}
                    </button>
                ))}
            </div>

            <div className="ww-controls">
                <button className="ww-ctrl-btn" onClick={shuffle}>🔀</button>
                <button className="ww-ctrl-btn" onClick={backspace} disabled={!currentWord}>⌫</button>
                <button className="ww-submit-btn" onClick={submitWord} disabled={!currentWord}>
                    {tx('enter')}
                </button>
            </div>

            {foundWords.length > 0 && (
                <div className="ww-word-list">
                    {foundWords.map(k => (
                        <div key={k} className="ww-found-chip" onClick={() => saveFoundWord(k)}>
                            {savingWord === k ? tx('saved') : (
                                <>
                                    {set.entries[k].word}
                                    <span className="ww-found-save-hint">🔖</span>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}